<template>
  <!-- Whether a welcome mail goes out depends on the account's opt-in (MIL-397). A mail going out
  is the intro under the title; no mail is a yellow warning at the top of the form. -->
  <BaseModal
    :modalTitle="$t('area.customer-account.users.create.title')"
    :modalIntro="welcomeEmailIntroText">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <InfoText
        v-if="!isWelcomeEmailEnabled"
        class="mb-4"
        theme="warning"
        :text="noWelcomeEmailWarningText" />

      <UiInputTextFieldVee
        name="firstName"
        :label="$t('form.label.first-name')"
        :placeholder="$t('form.placeholder.first-name')"
        :rules="['required']"
        isRequired
        v-model="form.firstName"
        @update:model-value="resetValidationMessages" />

      <UiInputTextFieldVee
        name="lastName"
        :label="$t('form.label.last-name')"
        :placeholder="$t('form.placeholder.last-name')"
        :rules="['required']"
        isRequired
        v-model="form.lastName"
        @update:model-value="resetValidationMessages" />

      <UiInputTextFieldVee
        name="email"
        :label="$t('form.label.email')"
        :placeholder="$t('form.placeholder.email')"
        :rules="['required', 'email']"
        isRequired
        type="email"
        v-model="form.email"
        @update:model-value="resetValidationMessages">
        <template
          v-if="EMU.isEmulatingFirestore"
          #trailingIcon>
          <button
            type="button"
            class="pointer-events-auto flex h-5 w-5 cursor-pointer items-center justify-center opacity-50 hover:opacity-100"
            v-tooltip="$t('W: Indsæt dummy-email')"
            @click="insertFakeEmail">
            <IconLucideSparkles class="h-5 w-5" />
          </button>
        </template>
      </UiInputTextFieldVee>

      <UiInputPhoneVee
        name="phone"
        :label="$t('form.label.phone')"
        :rules="['requiredPhone', 'phone']"
        isRequired
        v-model="form.phone"
        @update:model-value="resetValidationMessages" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useModalStore } from '@/stores/modalStore'
import { isWelcomeEmailEnabledForCustomer } from '@/globals/typescript/utils/customerOwner'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import {
  getNoWelcomeEmailWarningText,
  getWelcomeEmailIntroText,
} from '@/globals/typescript/utils/welcomeEmailNotice'
import {
  getEmailInUseByAnotherUserMessage,
  getErrorMessage,
} from '@/globals/typescript/utils/errorMessages'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import { EMU } from '@/globals/typescript/utils/host'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions } from 'pinia'
import type { PropType } from 'vue'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

type TCreateCustomerUserForm = {
  firstName: string
  lastName: string
  email: string
  phone: { number: number; iso2: string }
}

function createForm(): TCreateCustomerUserForm {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: { number: 0, iso2: 'dk' },
  }
}

export default {
  name: 'CreateCustomerUserBusinessModal',

  // A business or an insurance customer's user (MIL-402) — the API path follows `customer`.
  props: {
    customer: { type: Object as PropType<ICustomerRef>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      errorText: '',
      isLoading: false,
      EMU,
    }
  },

  computed: {
    isWelcomeEmailEnabled(): boolean {
      return isWelcomeEmailEnabledForCustomer(this.customer)
    },

    welcomeEmailIntroText(): string {
      return this.isWelcomeEmailEnabled ? getWelcomeEmailIntroText() : ''
    },

    noWelcomeEmailWarningText(): string {
      return getNoWelcomeEmailWarningText()
    },
  },

  methods: {
    ...mapActions(useCustomerUserStore, ['createCustomerUser']),
    ...mapActions(useModalStore, ['closeTop']),

    insertFakeEmail() {
      this.form.email = generateFakeEmail()
      this.resetValidationMessages()
    },

    resetValidationMessages() {
      this.errorText = ''
    },

    async onSubmit(): Promise<void> {
      this.resetValidationMessages()
      this.isLoading = true

      try {
        const result = await this.createCustomerUser(this.customer, this.form)

        if (result.success) {
          // The user exists either way — a failed mail is said out loud rather than shown as a
          // failed creation, which a retry would only turn into "email already exists".
          if (result.welcomeEmailFailed) {
            useToast().error(
              this.$t(
                'W: Bruger oprettet, men velkomstmailen kunne ikke sendes'
              )
            )
          } else {
            useToast().success(
              this.isWelcomeEmailEnabled
                ? this.$t('W: Bruger oprettet – velkomstmail sendt')
                : this.$t('W: Bruger oprettet – ingen velkomstmail sendt')
            )
          }
          this.closeTop()
          return
        }

        // An email already used by another user — of either customer type, or lab staff — gets its
        // own message, inline and as a toast, rather than the generic error (MIL-402).
        this.errorText =
          result.code === 'already-exists'
            ? getEmailInUseByAnotherUserMessage()
            : getErrorMessage(result.code ?? '')
        useToast().error(this.errorText)
      } catch (error) {
        reportUnexpectedError(error, { operation: 'customer-user-create' })
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },

    formResetCallback(): boolean {
      if (this.errorText) {
        return false
      }
      this.form = createForm()
      return true
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
